import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import StatusBadge from "@/components/common/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const VENDORS = ["Kantech", "Avigilon", "Lenel OnGuard", "Magos Radar"];
const MS_PER_DAY = 86400000;
const EXPIRY_WARNING_DAYS = 60;

export default function HardwarePage() {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor: "Kantech", model: "", serial: "", site: "", firmware: "", license_expiry: "", status: "operational" });

  const load = useCallback(() => {
    return api.get("/hardware").then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/hardware", form);
      toast.success("Asset added.");
      setOpen(false);
      setForm({ vendor: "Kantech", model: "", serial: "", site: "", firmware: "", license_expiry: "", status: "operational" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/hardware/${id}`); load(); toast.success("Deleted."); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const visible = rows.filter(r => !filter || r.vendor === filter);

  const expiryBadge = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const days = Math.round((d.getTime() - Date.now()) / MS_PER_DAY);
    if (days < 0) return <span className="font-mono text-[11px] text-[#FF3D00]">{d.toISOString().slice(0,10)} · EXPIRED</span>;
    if (days < EXPIRY_WARNING_DAYS) return <span className="font-mono text-[11px] text-[#FFC400]">{d.toISOString().slice(0,10)} · {days}d</span>;
    return <span className="font-mono text-[11px] text-muted-foreground">{d.toISOString().slice(0,10)}</span>;
  };

  return (
    <div className="p-6 lg:p-10 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">04 · Asset Inventory</div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white mt-2" data-testid="hardware-title">{t("hardware.title")}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1" data-testid="hardware-vendor-filter">
            <button onClick={() => setFilter("")} className={chip(filter === "")} data-testid="hardware-filter-all">ALL</button>
            {VENDORS.map(v => <button key={v} onClick={() => setFilter(v)} className={chip(filter === v)} data-testid={`hardware-filter-${v.toLowerCase().replace(/\s+/g, '-')}`}>{v.toUpperCase()}</button>)}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-2 px-5 h-10 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors" data-testid="hardware-new-button">
                <Plus className="w-4 h-4" /> {t("hardware.new")}
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle className="font-mono tracking-widest uppercase text-sm text-white">{t("hardware.new")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="hardware-form">
                <div className="grid grid-cols-2 gap-3">
                  <F label="Vendor"><select value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} className={inp} data-testid="hardware-vendor-select">{VENDORS.map(v => <option key={v}>{v}</option>)}</select></F>
                  <F label="Model"><input required value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} className={inp} data-testid="hardware-model-input" /></F>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Serial"><input required value={form.serial} onChange={e => setForm({ ...form, serial: e.target.value })} className={inp} data-testid="hardware-serial-input" /></F>
                  <F label="Site"><input required value={form.site} onChange={e => setForm({ ...form, site: e.target.value })} className={inp} data-testid="hardware-site-input" /></F>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <F label="Firmware"><input value={form.firmware} onChange={e => setForm({ ...form, firmware: e.target.value })} className={inp} data-testid="hardware-firmware-input" /></F>
                  <F label="License expiry"><input type="date" value={form.license_expiry?.slice(0,10) || ""} onChange={e => setForm({ ...form, license_expiry: e.target.value })} className={inp} data-testid="hardware-expiry-input" /></F>
                  <F label="Status"><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className={inp} data-testid="hardware-status-select">{["operational","warning","critical","offline"].map(s => <option key={s}>{s}</option>)}</select></F>
                </div>
                <DialogFooter><button type="submit" className="h-10 px-5 rounded-sm bg-primary text-primary-foreground font-medium" data-testid="hardware-form-submit">{t("common.save")}</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="border border-border rounded-sm bg-card overflow-hidden">
        <table className="w-full text-left" data-testid="hardware-table">
          <thead>
            <tr className="border-b border-border text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
              <th className="px-6 py-3">{t("hardware.columns.vendor")}</th>
              <th className="px-4 py-3">{t("hardware.columns.model")}</th>
              <th className="px-4 py-3">{t("hardware.columns.serial")}</th>
              <th className="px-4 py-3">{t("hardware.columns.site")}</th>
              <th className="px-4 py-3">{t("hardware.columns.firmware")}</th>
              <th className="px-4 py-3">{t("hardware.columns.license")}</th>
              <th className="px-4 py-3 text-right">{t("hardware.columns.status")}</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => (
              <tr key={r.asset_id} className="border-b border-border/60 last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`hardware-row-${r.asset_id}`}>
                <td className="px-6 py-4 text-sm text-white">{r.vendor}</td>
                <td className="px-4 py-4 text-sm text-secondary-foreground font-mono">{r.model}</td>
                <td className="px-4 py-4 text-sm text-muted-foreground font-mono">{r.serial}</td>
                <td className="px-4 py-4 text-sm text-secondary-foreground">{r.site}</td>
                <td className="px-4 py-4 font-mono text-[12px] text-primary">{r.firmware || "—"}</td>
                <td className="px-4 py-4">{expiryBadge(r.license_expiry)}</td>
                <td className="px-4 py-4 text-right"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-4 text-right"><button onClick={() => remove(r.asset_id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`hardware-delete-${r.asset_id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={8} className="px-6 py-10 text-center text-sm text-muted-foreground">No assets.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm text-white";
const chip = (active) => `px-3 h-9 rounded-sm border text-[10px] font-mono tracking-[0.22em] uppercase transition-colors ${active ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"}`;
function F({ label, children }) { return <label className="block"><div className="mb-1.5 text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">{label}</div>{children}</label>; }
